/**
 * English fallbacks for message ids this front-end introduced.
 *
 * Translations are fetched from the platform, so an id the backend has not shipped yet
 * renders as its own id — "filter__sheet_apply" in the middle of a button. These are
 * merged *under* the fetched translations in LanguageProvider, so the platform always
 * wins and this only ever fills a hole.
 *
 * The rule that goes with this file: a new id gets an entry here in the same commit
 * that introduces it. An id that already exists in the platform's translations does not
 * belong here — reuse the existing one instead of adding a synonym.
 */
export const DEFAULT_MESSAGES: Record<string, string> = {
  // Home
  home__live_now: "{count} live now",

  // Filter sheets
  filter__sheet_apply: "Apply",
  filter__sheet_clear: "Clear",
  filter__sheet_close: "Close without changing anything",
  filter__sheet_back: "Back to more filters",
  filter__selected_count: "{count} selected",
  filter__more_title: "More filters",
  filter__more_subtitle:
    "{count, plural, =0 {Thermometer and gender stay on the feed} one {# on · thermometer and gender stay on the feed} other {# on · thermometer and gender stay on the feed}}",
  filter__search_language: "Search a language",
  filter__subtitle_thermometers: "Several can be on at once",
  filter__subtitle_genders: "Several can be on at once",
  filter__subtitle_age_ranges: "Several can be on at once",
  filter__subtitle_languages: "Several can be on at once",
  filter__subtitle_website_uses: "What members say they're here for",
  filter__subtitle_relationship_sought: "What members are looking for",
  filter__subtitle_has_fanclub: "Paid content behind a subscription",
  filter__any_thermometer: "Any heat",
  filter__any_gender: "Anyone",
  filter__any_age: "Any age",
  filter__any_language: "Any language",
  filter__any_website_use: "Any use of the site",
  filter__any_relationship: "Any relationship",
  filter__any_fanclub: "Free and paid",
  filter__explain_thermometer_soft: "Suggestive, nothing shown",
  filter__explain_thermometer_hot: "Partial nudity",
  filter__explain_thermometer_hard: "Nothing held back · needs a verified account",
  filter__explain_fanclub_yes: "Members who sell access to their snaps",
  filter__explain_fanclub_no: "Everything they post is free",

  // Appearance and vibe
  parameters__appearance: "Appearance",
  appearance__label: "Appearance",
  appearance__light: "Light",
  appearance__dark: "Dark",
  appearance__system: "System",
  vibe__label: "Vibe",
  vibe__sharp: "Sharp",
  vibe__default: "Default",
  vibe__soft: "Soft",
  vibe__explain: "How rounded everything is, across the whole site.",

  // Profile blocks
  profile__online_now: "Online now",
  profile__content: "Content",
  profile__album: "Album",
  profile__album_empty: "Nothing here yet.",
  profile__fanclub_only: "Fan club only",
  profile__fanclub_member: "You're a member",
  profile__fanclub_pitch: "Permanent photos and videos, kept for members of the club.",
  profile__fanclub_flames: "Flames needed",
  profile__fanclub_messages: "Messages needed",
  profile__fanclub_how: "How to join",
  profile__arrange: "Arrange my profile",
  profile__arrange_done: "Done",
  profile__arrange_hint:
    "Reorder your blocks and choose which ones visitors see. Identity always comes first.",
  profile__arrange_more: "More blocks are on the way",
  profile__block_pinned: "Always shown, always first",
  profile__block_identity: "Identity",
  profile__block_identity_hint: "Cover, avatar, name, bio",
  profile__block_highlights: "Highlights",
  profile__block_highlights_hint: "The sections of your profile",
  profile__block_fanclub_hint: "Your club and how to join it",
  profile__block_about_hint: "What you're looking for",
  profile__block_content_hint: "Snaps, album and fan club",

  // Gift sheet (26). The flow's own strings reuse the chat tray's platform ids
  // (gifttray__header_common_boost, giftmessage__*); these fill only what the
  // profile entry point adds.
  gifttray__title: "Send a gift",
  gifttray__subtitle: "To {member}",
  gifttray__balance: "{count, number} tokens",
  gifttray__cost_after: "{cost, number} tokens · {left, number} left after",
  gifttray__back: "Back",
  gifttray__done: "Done",
  gifttray__sent_summary: "+{flames, number} flames for {member} · {left, number} tokens left.",
  gifttray__sent_rank: "Now #{rank, number}.",
  gifttray__sent_message: "You sent a gift",

  // The You page. Destinations reuse the menu's own ids (menu__my_profile,
  // menu__my_epheme, menu__my_settings, …); these are only what the hub adds.
  you__title: "You",
  you__activity:
    "<b>{visits, plural, one {# visit} other {# visits}}</b> and <b>{likes, plural, one {# like} other {# likes}}</b> since your last look",
  you__verification: "Verification",
  you__verification_progress: "{done} of {total} done",
  you__verification_done: "Done",
  you__verification_todo: "Not done yet",
  you__not_started: "Not started",
  you__start: "Start",
  you__age_verification: "Age verification",
  you__age_verification_note:
    "Without it you cannot see or post sexy and explicit snaps.",
  you__certification_note:
    "A photo holding a sign with your username. Certified profiles carry the badge and rank higher.",
  you__contributor_note:
    "An identity document, required before you can be paid or run a fan club. Stored by our payment provider, never shown on your profile.",
  you__view_as_visitor: "View as visitor",
  you__recent_snaps: "Recent snaps",
  you__see_all: "See all",
  you__help_legal: "Help and legal",
  you__get_help: "Get help",
  you__legal: "Legal",

  // The composer's fork and details screens. The rest of the flow reuses the
  // uploader's shipped ids (uploader__soft, uploader__recipients, …).
  uploader__pick_hint: "Photos and videos — up to {max} at once for an album post, one for a snap.",
  uploader__wrong_type: "Choose photos or videos.",
  uploader__too_big: "A file is too big — 300 MB at most.",
  uploader__kind_title: "New post",
  uploader__kind_items: "{count, plural, one {# item} other {# items}}",
  uploader__kind_add_hint: "Add another and it becomes an album post — a snap is one media.",
  uploader__kind_snap_title: "Snap",
  uploader__kind_album_title: "Album",
  uploader__kind_snap_single: "One photo or one video, on its own",
  uploader__kind_snap_once: "Each member can open it once",
  uploader__kind_snap_feed: "Shows in the feed and on your profile",
  uploader__kind_snap_expires: "Deleted automatically after 30 days — sooner if you delete it",
  uploader__kind_snap_challenge: "The only kind that can enter a challenge",
  uploader__kind_snap_disabled: "A snap is one media — remove down to one item to post one.",
  uploader__kind_album_batch: "One to ten items, each posted to your album",
  uploader__kind_album_stays: "Stays until you delete it",
  uploader__kind_album_profile: "Seen on your profile only — never in the feed",
  uploader__continue_snap: "Continue as a snap",
  uploader__continue_album: "Continue as an album post",
  uploader__change_media: "Change media",
  uploader__before_post: "Before you post",
  uploader__epheme_note:
    "A snap gives each member one look and deletes itself after 30 days. Anything you want kept belongs in the album.",
  uploader__album_hint: "Up to {max} items, posted together to your album.",
  uploader__album_note:
    "Album posts stay on your profile until you delete them, and never appear in the feed.",
  uploader__album_partial: "{count, plural, one {# item was} other {# items were}} posted before the error.",
  uploader__post_snap: "Post the snap",
  uploader__post_album: "Post {count, plural, one {# item} other {# items}}",

  // The rebuilt viewer. Everything else it says reuses shipped ids
  // (ephemeitem__thermometer_*, reportpopin__*, memberitem__follow, menu__login).
  ephemeviewer__end_title: "End of this batch",
  ephemeviewer__end_more: "That's everything you opened with — there are more waiting.",
  ephemeviewer__end_done: "You've seen everything that matches. Widen the filters to keep going.",
  ephemeviewer__load_more: "Load {count} more",
  ephemeviewer__only_look: "Your only look",
  ephemeviewer__seen_look: "Seen — this was your look",
  ephemeviewer__days_left: "{days} d left",
  ephemeviewer__scroll_next: "Scroll for the next",
  ephemeviewer__reply: "Reply",
  ephemeviewer__gift: "Gift",
  ephemeviewer__profile: "Profile",
  ephemeviewer__up_next: "Next",
  ephemeviewer__keys_move: "move",
  ephemeviewer__keys_like: "like",
  ephemeviewer__keys_close: "close",
  ephemeviewer__chat_guest: "Log in to open the chat",

  // The comment thread on contents — the album is public and permanent, so it
  // carries comments where an epheme carries a chat. Endpoints not shipped yet.
  ephemeviewer__comments_count: "{count, plural, one {# comment} other {# comments}}",
  ephemeviewer__comments_sort_top: "Top",
  ephemeviewer__comments_sort_recent: "Recent",
  ephemeviewer__comment_placeholder: "Add a comment…",
  ephemeviewer__comments_empty: "Be the first to comment.",
  ephemeviewer__comment_author: "AUTHOR",
  ephemeviewer__comment_replying_to: "Replying to {name}"
};

export default DEFAULT_MESSAGES;
